import { z } from "zod";

// ============================================================
// MCP Response Envelope
//
// Wire-format contract between the iamu MCP server and agent
// callers (Claude Code, other MCP hosts). Tools return:
//
//   {
//     structuredContent: { ...data, _meta: { v: 1, ... } },
//     content: [
//       { type: "text", text: prose, annotations: { audience: ["user"] } },
//       ...resource_links // each annotated audience: ["assistant"]
//     ]
//   }
//
// Errors:
//   {
//     isError: true,
//     structuredContent: { ...errorPayload, _meta: { v: 1, ... } },
//     content: [{ type: "text", text: "[tool] CODE: msg",
//                 annotations: { audience: ["user"] } }]
//   }
//
// Single source of truth: `structuredContent`. The MCP SDK validates
// it against the tool's declared `outputSchema`. Content blocks are
// human-facing only (prose for transcript readers, resource_links
// for lazy entity fetch).
//
// `_meta` is the spec-blessed (2025-11-25) place to attach
// envelope-level metadata to a structured object — `v` (envelope
// version, bumped only on non-additive shape changes), `tool`,
// `generated_at`, optional `pagination`, `retry_state`, `warnings`,
// `cloud_mirrored`.
// ============================================================

export const ENVELOPE_VERSION = 1 as const;

export const DEFAULT_PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 100;

// ------------------------------------------------------------
// Error codes — wire-level Zod enum mirroring IamuErrorCode in
// ./errors.ts. Keep in sync: every wire code must have a matching
// IamuError subclass and a ERROR_RETRY_HINTS entry.
// ------------------------------------------------------------

export const ErrorCode = z.enum([
  "not_found",
  "cloud_unreachable",
  "invalid_uuid_in_local_mode",
  "auth_required",
  "config_error",
  "validation_error",
  "conflict",
  "rate_limited",
  "cursor_version_mismatch",
  "internal",
]);
export type ErrorCode = z.infer<typeof ErrorCode>;

// ------------------------------------------------------------
// Schemas
// ------------------------------------------------------------

export const WarningSchema = z.object({
  code: z.string().min(1),
  message: z.string().min(1),
  hint: z.string().optional(),
});
export type Warning = z.infer<typeof WarningSchema>;

export const RetryStateSchema = z.object({
  pending_count: z.number().int().nonnegative(),
  by_entity_type: z.record(z.string(), z.number().int().nonnegative()),
  oldest_ts: z.string().datetime(),
});
export type RetryState = z.infer<typeof RetryStateSchema>;

export const PaginationSchema = z.object({
  has_more: z.boolean(),
  next_cursor: z.string().nullable(),
  limit: z.number().int().positive(),
  returned: z.number().int().nonnegative(),
});
export type Pagination = z.infer<typeof PaginationSchema>;

/** The `_meta` field that lives ON `structuredContent` per MCP spec
 *  2025-11-25. Carries envelope-level metadata so the `data` payload
 *  stays clean entity-shape. `v` is the envelope version — bump on
 *  non-additive shape changes; agents check this before parsing. */
export const EnvelopeMetaSchema = z.object({
  v: z.literal(ENVELOPE_VERSION),
  tool: z.string().min(1),
  generated_at: z.string().datetime(),
  cloud_mirrored: z.boolean().optional(),
  pagination: PaginationSchema.optional(),
  retry_state: RetryStateSchema.optional(),
  warnings: z.array(WarningSchema).optional(),
});
export type EnvelopeMeta = z.infer<typeof EnvelopeMetaSchema>;

/** The structured envelope. `data` fields are spread at the top level
 *  of `structuredContent`; envelope-level metadata lives on `_meta`.
 *  Each migrated tool declares an `outputSchema` describing the full
 *  shape (data fields + `_meta`). */
export type McpEnvelope<
  T extends Record<string, unknown> = Record<string, unknown>,
> = T & {
  _meta: EnvelopeMeta;
};

/** Build the Zod outputSchema for a tool: combines its data shape
 *  with the standard `_meta` field. Tools call this in registerTool.
 *  Generic over the raw shape so the returned schema preserves
 *  field-level types through `.parse()`. */
export function mcpEnvelopeSchema<T extends z.ZodRawShape>(
  dataSchema: z.ZodObject<T>,
) {
  return dataSchema.extend({ _meta: EnvelopeMetaSchema });
}

export const RetryHintSchema = z.object({
  retryable: z.boolean(),
  retry_after_ms: z.number().int().positive().optional(),
  max_attempts_suggested: z.number().int().positive().optional(),
});
export type RetryHint = z.infer<typeof RetryHintSchema>;

export const McpErrorSchema = z.object({
  code: ErrorCode,
  message: z.string().min(1).max(240),
  retry_hint: RetryHintSchema.optional(),
  suggested_action: z.string().optional(),
  remediation_tool: z.string().optional(),
  details: z.record(z.string(), z.unknown()).optional(),
});
export type McpError = z.infer<typeof McpErrorSchema>;

// ------------------------------------------------------------
// Cursor: opaque base64url-encoded JSON
// ------------------------------------------------------------

export const CURSOR_VERSION = 1 as const;

const CursorPayloadSchema = z.object({
  after_id: z.string().min(1),
  sort: z.string().min(1),
  v: z.literal(CURSOR_VERSION),
});
export type CursorPayload = z.infer<typeof CursorPayloadSchema>;

function base64urlEncode(s: string): string {
  return Buffer.from(s, "utf8")
    .toString("base64")
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function base64urlDecode(s: string): string {
  const padded = s.replace(/-/g, "+").replace(/_/g, "/");
  const pad =
    padded.length % 4 === 0 ? "" : "=".repeat(4 - (padded.length % 4));
  return Buffer.from(padded + pad, "base64").toString("utf8");
}

export function encodeCursor(payload: Omit<CursorPayload, "v">): string {
  const withVersion: CursorPayload = { ...payload, v: CURSOR_VERSION };
  CursorPayloadSchema.parse(withVersion);
  return base64urlEncode(JSON.stringify(withVersion));
}

export class CursorVersionError extends Error {
  constructor(got: unknown) {
    super(
      `Cursor version mismatch: expected ${CURSOR_VERSION}, got ${String(got)}`,
    );
    this.name = "CursorVersionError";
  }
}

/** Hard cap to prevent a hostile or malformed cursor from forcing the
 *  server to allocate + JSON-parse a multi-megabyte payload. A real
 *  cursor encodes ~80 bytes (uuid + sort tag + version), so 2 KB is
 *  comfortably above the worst-case real payload while bounding DoS
 *  surface area. */
export const MAX_CURSOR_LENGTH = 2048;

export function decodeCursor(cursor: string): CursorPayload {
  if (cursor.length > MAX_CURSOR_LENGTH) {
    throw new Error(
      `Cursor exceeds ${MAX_CURSOR_LENGTH} bytes; refusing to decode`,
    );
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(base64urlDecode(cursor));
  } catch {
    throw new Error("Cursor is not valid base64url-encoded JSON");
  }
  const result = CursorPayloadSchema.safeParse(parsed);
  if (!result.success) {
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "v" in parsed &&
      (parsed as { v: unknown }).v !== CURSOR_VERSION
    ) {
      throw new CursorVersionError((parsed as { v: unknown }).v);
    }
    throw new Error(`Invalid cursor payload: ${result.error.message}`);
  }
  return result.data;
}

// ------------------------------------------------------------
// Entity URIs: iamu://<type>/<id> or iamu://<type>/local/<slug>
// ------------------------------------------------------------

export type UriEntityType =
  | "project"
  | "scenario"
  | "outline"
  | "persona"
  | "walkthrough"
  | "finding"
  | "capability"
  | "source"
  | "constraint";

const ENTITY_TYPES: ReadonlySet<UriEntityType> = new Set<UriEntityType>([
  "project",
  "scenario",
  "outline",
  "persona",
  "walkthrough",
  "finding",
  "capability",
  "source",
  "constraint",
]);

export function entityUri(
  type: UriEntityType,
  id: string,
  opts: { local?: boolean } = {},
): string {
  if (!id || id.includes("/")) {
    throw new Error(`Invalid entity id for ${type}: ${id}`);
  }
  return opts.local ? `iamu://${type}/local/${id}` : `iamu://${type}/${id}`;
}

export type ParsedEntityUri = {
  type: UriEntityType;
  id: string;
  local: boolean;
};

/** Parse an `iamu://` URI. Returns null on malformed input (callers decide
 *  whether to raise VALIDATION_ERROR). Accepts bare ids as a passthrough
 *  convenience: `parseEntityUri("abc", "finding")` returns
 *  `{ type: "finding", id: "abc", local: false }` when a type hint is given. */
export function parseEntityUri(input: string): ParsedEntityUri | null {
  if (!input.startsWith("iamu://")) return null;
  const rest = input.slice("iamu://".length);
  const segments = rest.split("/").filter(Boolean);
  if (segments.length < 2) return null;

  const type = segments[0] as UriEntityType;
  if (!ENTITY_TYPES.has(type)) return null;

  if (segments[1] === "local") {
    if (segments.length !== 3) return null;
    return { type, id: segments[2]!, local: true };
  }
  if (segments.length !== 2) return null;
  return { type, id: segments[1]!, local: false };
}
